// YouTube API integration
// This module handles YouTube channel analytics and data fetching

import { YOUTUBE_API_KEY } from '../../../config/api.config';
import { OutreachCategory } from '../../../types/outreach';
import type { ReactionChannel, PlaylistingChannel } from '../../../types/outreach';
import { getOutreachItem, updateOutreachItem } from '../firestore/crud';

// Interface for YouTube channel details
export interface YouTubeChannelDetails {
  id: string;
  title: string;
  description: string;
  thumbnails: {
    default?: { url: string; width: number; height: number };
    medium?: { url: string; width: number; height: number };
    high?: { url: string; width: number; height: number };
  };
  storedThumbnailUrl?: string | null; // URL to the stored thumbnail in Firebase Storage
  customUrl?: string;
  publishedAt: string;
  subscriberCount: string;
  viewCount: number;
  videoCount: number;
  likes?: string;
}

/**
 * Extract YouTube channel ID from URL
 * Handles various YouTube URL formats
 *
 * @param url YouTube channel URL
 * @returns Channel ID or null if not found
 */
const extractYouTubeChannelId = (url: string): string | null => {
  try {
    console.log("Extracting channel ID from URL:", url);

    // Handle URLs without protocol
    if (!url.startsWith("http")) {
      url = "https://" + url;
    }

    const urlObj = new URL(url);
    const pathname = urlObj.pathname;

    console.log("URL pathname:", pathname);

    // Handle different YouTube URL formats
    if (pathname.startsWith("/channel/")) {
      const channelId = pathname.split("/channel/")[1].split("/")[0];
      console.log("Extracted channel ID (channel format):", channelId);
      return channelId;
    } else if (pathname.startsWith("/c/")) {
      const customName = pathname.split("/c/")[1].split("/")[0];
      console.log("Extracted custom name (c/ format):", customName);
      return "c/" + customName;
    } else if (pathname.startsWith("/@")) {
      const handleName = pathname.split("/@")[1].split("/")[0];
      console.log("Extracted handle name (@ format):", handleName);
      return "@" + handleName;
    } else if (pathname.startsWith("/user/")) {
      const userName = pathname.split("/user/")[1].split("/")[0];
      console.log("Extracted user name (user format):", userName);
      return userName;
    } else if (pathname.includes("/watch")) {
      // Extract video ID from watch URL
      const urlParams = new URLSearchParams(urlObj.search);
      const videoId = urlParams.get("v");
      console.log("Found watch URL, using video ID:", videoId);
      return videoId;
    } else if (urlObj.hostname === "youtu.be") {
      // Handle youtu.be short URLs
      const videoId = pathname.substring(1); // Remove leading slash
      console.log("Found youtu.be URL, using video ID:", videoId);
      return videoId;
    } else if (pathname.includes("/videos") || pathname === "/") {
      // Main channel page
      const hostname = urlObj.hostname;
      if (hostname.includes("youtube.com")) {
        // Try to extract from hostname or use pathname
        const channelPath =
          pathname === "/" ? urlObj.hostname : pathname.split("/")[1];
        console.log("Using channel path from main page:", channelPath);
        return channelPath;
      }
    }

    console.log("Could not extract channel ID from URL:", url);
    return null;
  } catch (error) {
    console.error("Error extracting YouTube channel ID:", error);
    return null;
  }
};

/**
 * Download and store a thumbnail image in Firebase Storage
 * Currently disabled to avoid CORS issues in local development
 *
 * @param imageUrl URL of the image to download
 * @param channelId Channel ID to associate with the image
 * @returns URL of the stored image or null
 */
const downloadAndStoreThumbnail = async (
  imageUrl: string,
  _channelId: string
): Promise<string | null> => {
  console.log(
    `Thumbnail storage disabled to avoid CORS issues. Using original URL: ${imageUrl}`
  );

  // Simply return the original image URL without trying to store it
  // This avoids the CORS issues when running in local development
  return null; // Return null to force using the original YouTube URLs
};

/**
 * Fetch YouTube videos and their likes
 *
 * @param channelId YouTube channel ID
 * @returns Total number of likes across all videos
 */
const fetchYouTubeVideosAndLikes = async (
  channelId: string
): Promise<number> => {
  try {
    // First get the uploads playlist ID for the channel
    const channelResponse = await fetch(
      `https://www.googleapis.com/youtube/v3/channels?part=contentDetails&id=${channelId}&key=${YOUTUBE_API_KEY}`
    );
    const channelData = await channelResponse.json();

    if (!channelData.items || channelData.items.length === 0) {
      console.error("No channel data found");
      return 0;
    }

    const uploadsPlaylistId =
      channelData.items[0].contentDetails.relatedPlaylists.uploads;

    // Get videos from the uploads playlist
    const videosResponse = await fetch(
      `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&maxResults=50&playlistId=${uploadsPlaylistId}&key=${YOUTUBE_API_KEY}`
    );
    const videosData = await videosResponse.json();

    if (!videosData.items || videosData.items.length === 0) {
      console.error("No videos found");
      return 0;
    }

    // Extract video IDs
    const videoIds = videosData.items
      .map((item: any) => item.snippet.resourceId.videoId)
      .join(",");

    // Get video statistics including likes
    const statsResponse = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?part=statistics&id=${videoIds}&key=${YOUTUBE_API_KEY}`
    );
    const statsData = await statsResponse.json();

    if (!statsData.items || statsData.items.length === 0) {
      console.error("No video statistics found");
      return 0;
    }

    // Calculate total likes
    let totalLikes = 0;
    statsData.items.forEach((item: any) => {
      if (item.statistics && item.statistics.likeCount) {
        totalLikes += parseInt(item.statistics.likeCount);
      }
    });

    return totalLikes;
  } catch (error) {
    console.error("Error fetching YouTube videos and likes:", error);
    return 0;
  }
};

/**
 * Fetch YouTube channel details including description and thumbnails
 *
 * @param channelUrl YouTube channel URL
 * @returns Channel details or null if not found
 */
export const fetchYouTubeChannelDetails = async (
  channelUrl: string
): Promise<YouTubeChannelDetails | null> => {
  try {
    const channelId = extractYouTubeChannelId(channelUrl);
    console.log("Extracted channel ID:", channelId);
    if (!channelId) {
      console.error("Could not extract channel ID from URL:", channelUrl);
      return null;
    }

    // First try with channel ID
    let apiUrl = `https://www.googleapis.com/youtube/v3/channels?part=snippet,statistics,brandingSettings&id=${channelId}&key=${YOUTUBE_API_KEY}`;

    // If it's a custom URL, try with forHandle
    if (channelId.startsWith("@") || channelId.startsWith("c/")) {
      const forUsername = channelId.replace("@", "").replace("c/", "");
      console.log("Using forHandle:", forUsername);
      apiUrl = `https://www.googleapis.com/youtube/v3/channels?part=snippet,statistics,brandingSettings&forHandle=${forUsername}&key=${YOUTUBE_API_KEY}`;
      console.log("Updated API URL for custom URL:", apiUrl);
    }

    const response = await fetch(apiUrl);
    const data = await response.json();
    console.log("YouTube API response:", data);

    if (data.items && data.items.length > 0) {
      const channel = data.items[0];
      const statistics = channel.statistics;
      const snippet = channel.snippet;

      // Get total likes
      let totalLikes = "0";
      try {
        totalLikes = await fetchYouTubeVideosAndLikes(channelId).then((likes) =>
          likes.toString()
        );
      } catch (error) {
        console.error("Error fetching likes:", error);
      }

      // Download and store the thumbnail image in Firebase Storage
      let storedThumbnailUrl = null;
      if (
        snippet.thumbnails &&
        snippet.thumbnails.high &&
        snippet.thumbnails.high.url
      ) {
        try {
          console.log(
            "Attempting to download and store thumbnail:",
            snippet.thumbnails.high.url
          );
          storedThumbnailUrl = await downloadAndStoreThumbnail(
            snippet.thumbnails.high.url,
            channel.id
          );
          console.log(
            "Successfully stored thumbnail, URL:",
            storedThumbnailUrl
          );
        } catch (error) {
          console.error("Error storing thumbnail:", error);
        }
      } else {
        console.log(
          "No high-quality thumbnail available for channel:",
          channel.id
        );
      }

      return {
        id: channel.id,
        title: snippet.title,
        description: snippet.description,
        thumbnails: snippet.thumbnails,
        storedThumbnailUrl: storedThumbnailUrl,
        customUrl: snippet.customUrl,
        publishedAt: snippet.publishedAt,
        subscriberCount: statistics.subscriberCount,
        viewCount: parseInt(statistics.viewCount),
        videoCount: parseInt(statistics.videoCount),
        likes: totalLikes,
      };
    }

    return null;
  } catch (error) {
    console.error("Error fetching YouTube channel details:", error);
    return null;
  }
};

/**
 * Fetch YouTube channel analytics
 *
 * @param channelUrl YouTube channel URL
 * @returns Channel analytics or null if not found
 */
export const fetchYouTubeAnalytics = async (
  channelUrl: string
): Promise<{
  subscriberCount: string;
  viewCount: number;
  videoCount: number;
} | null> => {
  try {
    const channelId = extractYouTubeChannelId(channelUrl);

    if (!channelId) {
      console.error("Could not extract channel ID from URL:", channelUrl);
      return null;
    }

    // First try with channel ID
    let apiUrl = `https://www.googleapis.com/youtube/v3/channels?part=statistics%2Csnippet&id=${channelId}&key=${YOUTUBE_API_KEY}`;

    // If it's a custom URL, try with forHandle
    if (channelId.startsWith("@") || channelId.startsWith("c/")) {
      const forUsername = channelId.replace("@", "").replace("c/", "");
      apiUrl = `https://www.googleapis.com/youtube/v3/channels?part=statistics%2Csnippet&forHandle=${forUsername}&key=${YOUTUBE_API_KEY}`;
    }

    const response = await fetch(apiUrl);
    const data = await response.json();
    console.log(data);

    if (data.items && data.items.length > 0) {
      const statistics = data.items[0].statistics;
      return {
        subscriberCount: statistics.subscriberCount,
        viewCount: parseInt(statistics.viewCount),
        videoCount: parseInt(statistics.videoCount),
      };
    }

    return null;
  } catch (error) {
    console.error("Error fetching YouTube analytics:", error);
    return null;
  }
};

/**
 * Update YouTube channel analytics
 *
 * @param channelId Channel ID to update
 * @param category Category the channel belongs to
 * @returns True if successful, false otherwise
 */
export const updateYouTubeChannelAnalytics = async (
  channelId: string,
  category: OutreachCategory
): Promise<boolean> => {
  try {
    console.log(
      `Updating analytics for channel ID ${channelId} in category ${category}`
    );

    // Get the channel based on category
    let channel;
    if (category === OutreachCategory.REACTION_CHANNELS) {
      channel = await getOutreachItem<ReactionChannel>(category, channelId);
    } else if (category === OutreachCategory.PLAYLISTING_CHANNELS) {
      channel = await getOutreachItem<PlaylistingChannel>(category, channelId);
    } else {
      console.error(`Invalid category for YouTube channel: ${category}`);
      return false;
    }

    if (!channel || !channel.channelUrl) {
      console.error("Channel not found or missing URL");
      return false;
    }

    console.log(
      `Fetching analytics for channel: ${channel.channelName} (${channel.channelUrl})`
    );

    // Fetch analytics
    const analytics = await fetchYouTubeAnalytics(channel.channelUrl);

    if (!analytics) {
      console.error(
        "Failed to fetch analytics for channel:",
        channel.channelName
      );
      return false;
    }

    console.log(
      `Successfully fetched analytics for ${channel.channelName}:`,
      analytics
    );

    // Extract YouTube channel ID for fetching videos and likes
    const ytChannelId = extractYouTubeChannelId(channel.channelUrl);
    let totalLikes = 0;

    if (ytChannelId) {
      // Fetch videos and calculate total likes
      totalLikes = await fetchYouTubeVideosAndLikes(ytChannelId);
      console.log(`Total likes for ${channel.channelName}: ${totalLikes}`);
    }

    // Update the channel with new data
    const updateData = {
      subscriberCount: analytics.subscriberCount,
      viewCount: analytics.viewCount,
      videoCount: analytics.videoCount,
      likes: totalLikes.toString(), // Add the likes data
      lastUpdated: new Date().toISOString(),
    };

    console.log(`Updating channel ${channelId} with data:`, updateData);

    if (category === OutreachCategory.REACTION_CHANNELS) {
      await updateOutreachItem<ReactionChannel>(
        category,
        channelId,
        updateData
      );
    } else {
      await updateOutreachItem<PlaylistingChannel>(
        category,
        channelId,
        updateData
      );
    }

    console.log(`Successfully updated analytics for ${channel.channelName}`);
    return true;
  } catch (error) {
    console.error("Error updating YouTube channel analytics:", error);
    return false;
  }
};